// ADV-923: Hookdeck Outpost topic taxonomy.
//
// Single source of truth for the set of webhook topics Adva publishes. These
// strings are what customers subscribe to when registering a webhook endpoint,
// and what we pass as `topics` when creating Hookdeck destinations.
//
// Hookdeck Outpost itself does NOT manage topic creation via API — topics are
// declared at platform-config time on the Hookdeck side (env var TOPICS=… on
// self-host; equivalent config on cloud). The list below must be mirrored to
// that platform config on each environment (staging / production). The PR
// that lands this file is responsible for that operational handoff.
//
// Composition rule:
//   * Entity-shape events: `entity.{op}.{entity_type}` where op ∈
//     {created, updated, deleted}. Derived from core.log_activity_event's
//     event_type + entity_type mapping (see initial_schema.sql audit trigger).
//   * System events: emitted directly with a literal event_type such as
//     `import.started`. No entity_type suffix.

export const ENTITY_EVENT_OPS = ["created", "updated", "deleted"] as const;
export type EntityEventOp = (typeof ENTITY_EVENT_OPS)[number];

/**
 * Entity types that participate in the audit-event fan-out (and therefore
 * are eligible to be subscribed to via webhook). Sourced from the entity
 * tables tracked by `core.log_activity_event` plus their service-layer
 * `with-activity-event` wrappers.
 */
export const WEBHOOK_ENTITY_TYPES = [
  "customer",
  "contact",
  "team_member",
  "product",
  "location",
  "property",
  "opportunity",
  "proposal",
  "job",
  "invoice",
  "commission_plan",
  "field_definition",
  "business",
  "account",
  "warranty_term",
  "warranty",
  "warranty_claim",
  "service_plan",
  "service_agreement",
] as const;
export type WebhookEntityType = (typeof WEBHOOK_ENTITY_TYPES)[number];

/**
 * System-event topics — emitted with a literal event_type instead of the
 * entity.{op}.{entity_type} shape. Currently driven by the import pipeline.
 */
export const WEBHOOK_SYSTEM_EVENT_TYPES = [
  "import.started",
  "import.staged",
  "import.completed",
  "import.failed",
] as const;
export type WebhookSystemEventType =
  (typeof WEBHOOK_SYSTEM_EVENT_TYPES)[number];

/**
 * The full menu of topics customers can subscribe to. Order: entity topics
 * first (alphabetically by entity, then created/updated/deleted), then
 * system topics. Stable so config drift is easy to diff.
 */
export const WEBHOOK_TOPICS: readonly string[] = (() => {
  const entityTopics: string[] = [];
  for (const entity of WEBHOOK_ENTITY_TYPES) {
    for (const op of ENTITY_EVENT_OPS) {
      entityTopics.push(`entity.${op}.${entity}`);
    }
  }
  return [...entityTopics, ...WEBHOOK_SYSTEM_EVENT_TYPES];
})();

/**
 * Compose a Hookdeck topic string from an activity-event row's event_type +
 * entity_type pair.
 *
 *   * For `entity.created` / `entity.updated` / `entity.deleted` events the
 *     topic is `entity.{op}.{entity_type}`. If `entity_type` is null the
 *     topic falls back to the bare `event_type` so the publish call still
 *     succeeds (the destination subscription side decides whether to receive).
 *
 *   * For all other event types (e.g. `import.started`) the topic equals
 *     the event_type directly. Entity-type is ignored.
 *
 * Pure function — no side effects, safe to call in workers and tests.
 */
export function activityEventTopic(
  event_type: string,
  entity_type: string | null,
): string {
  const ENTITY_EVENT_PREFIX = "entity.";
  if (event_type.startsWith(ENTITY_EVENT_PREFIX) && entity_type) {
    const op = event_type.slice(ENTITY_EVENT_PREFIX.length);
    return `entity.${op}.${entity_type}`;
  }
  return event_type;
}

/**
 * Returns true when the topic string parses as a valid topic for this build.
 * Used by the endpoints CRUD service to reject filter values referencing
 * topics that Hookdeck won't accept. Treat as a soft check — Hookdeck remains
 * the authoritative validator of `topics` payloads on destination create.
 */
export function isKnownWebhookTopic(topic: string): boolean {
  return WEBHOOK_TOPICS.includes(topic);
}
